<template>
	<div class="StudyBasics" v-if="selectedStudy && currentTab">
		<v-card class="edit-study-basics v-card-element" outlined>
			<v-alert
				class="edit-alert"
				v-model="saveSuccess"
				dismissible
				type="success"
			>
				Changes have been saved.
			</v-alert>
			<v-alert
				class="edit-alert"
				v-model="hasErrors"
				dismissible
				type="error"
			>
				Changes not saved. One or more fields have errors.
			</v-alert>
			<div class="d-sr-only-focusable float-right text-decoration-underline" tabindex="0" @keyup.enter="focusToTop" @click="focusToTop" ref="firstFocusable" role="link">Go to top of page</div>
			<h2 class="left-align">Edit Study Basics</h2>
									<p>All sections on this page are required</p>
			<v-form
				ref="form"
				v-model="valid"
				lazy-validation
			>
				<v-row>
					<v-col md="2">
						<h3 class="left-align">Study name:</h3>
					</v-col>
					<v-col md="4">
						<v-text-field
							v-model="studyToSave.studyName"
							hide-details="auto"
							outlined
							counter="40"
							maxlength="40"
							:rules="studyNameRules"
							id="form-study-name"
							name="form-study-name"
							v-on:keyup="studyNameChar"
							:aria-label="'Study name: ' + studyNameLabel +  ' Description: ' + studyNameText"
						></v-text-field>
					</v-col>
					<v-col md="6" cols="12">
						<explanation-box
							:color="'green'"
							:displayText="studyNameText">
						</explanation-box>
					</v-col>
				</v-row>
				<v-divider inset></v-divider>
				<v-row>
					<v-col md="2">
						<h3 class="left-align">Study description:</h3>
					</v-col>
					<v-col md="4">
						<v-textarea
							v-model="studyToSave.description"
							hide-details="auto"
							outlined
							counter="400"
							maxlength="400"
							:rules="studyDescriptionRules"
							id="form-study-description"
							name="form-study-description"
							v-on:keyup="descriptionChar"
							:aria-label="'Study description: ' + descriptionLabel +  ' Description: ' + studyDescriptionText"
						></v-textarea>
					</v-col>
					<v-col md="6" cols="12">
						<explanation-box
							:color="'green'"
							:displayText="studyDescriptionText">
						</explanation-box>
					</v-col>
				</v-row>
				<v-divider inset></v-divider>
				<v-row >
								<v-col md="2">
									<h3 class="left-align">Study characteristics (Check all that apply):</h3>
								</v-col>
								<v-col md="4">
									<p class="left-align p2">Study population:</p>
									<fieldset class="border-0">
									<v-checkbox
										v-model="studyCharacteristics"
										v-for="(characteristic, i) in studyCharacteristicsCheckboxes"
										:key="characteristic.key + i"
										:label="characteristic.value"
										:value="characteristic.key"
										:hide-details="i !== studyCharacteristicsCheckboxes.length - 1"
										:rules="checkboxRules"
										:aria-label="'Study characteristics (Check all that apply): ' + characteristic.value +  ' Description: ' + studyCharacteristicsText"
									></v-checkbox>
									</fieldset>
								</v-col>
								<v-col  md="6" cols="12">
									<explanation-box
										:color="'green'"
										:fullBox=true
										:displayText="studyCharacteristicsText">
									</explanation-box>
								</v-col>
							</v-row>
							<v-divider inset></v-divider>
				<v-row>
					<v-col md="2">
						<h3 class="left-align">Study abbreviation:</h3>
					</v-col>
					<v-col md="4">
						<v-text-field
							:value="studyToSave.studyId"
							hide-details="auto"
							outlined
							counter="8"
							id="form-study-abbreviation"
							name="form-study-abbreviation"
							disabled
						></v-text-field>
					</v-col>
					<v-col md="6" cols="12">
						<explanation-box
							:color="'red'"
							:displayText="studyAbbreviationText">
						</explanation-box>
					</v-col>
				</v-row>
				<v-divider inset></v-divider>
				<v-row>
					<v-col md="2">
						<h3 class="left-align">Purpose of study:</h3>
					</v-col>
					<v-col md="4">
						<fieldset class="border-0">
						<v-radio-group
							v-model="studyPurpose"
							:rules="radioRules"
							id="form-study-purpose"
						>
							<v-radio
								v-for="(purpose, i) in this.$createStudyLists.studyPurposes"
								:key="purpose + i"
								:label="purpose"
								:value="purpose"
								:aria-label="'Purpose of study: ' + purpose +  ' Description: ' + purposeOfStudyText"
							></v-radio>
						</v-radio-group>
						<div v-if="studyPurpose && studyPurpose == this.$createStudyLists.studyPurposes[4]">
							<v-text-field
								v-model="studyPurposeOther"
								label="Other (specify)"
								hide-details="auto"
								outlined
								:aria-label="'Other (specify): ' + studyPurposeOther"
							></v-text-field>
						</div>
						</fieldset>
					</v-col>
					<v-col md="6" cols="12">
						<explanation-box
							:color="'green'"
							:displayText="purposeOfStudyText">
						</explanation-box>
					</v-col>
				</v-row>
				<v-divider inset></v-divider>
				<!-- Values to check for are based on list position in /assets/global_text/createStudyLists -->
				<div v-show="studyPurpose && studyPurpose == this.$createStudyLists.studyPurposes[0]">
					<v-row >
						<v-col md="2">
							<h3 class="left-align">Type of research? (Check all that apply)</h3>
						</v-col>
						<v-col md="4">
							<fieldset class="border-0">
							<v-checkbox
								v-model="researchType"
								v-for="(type, i) in researchTypeCheckboxes"
								:key="type + i"
								:label="type"
								:value="type"
								:rules="checkboxRules"
								:hide-details="i !== researchTypeCheckboxes.length - 1"
								:aria-label="'Type of research? (Check all that apply) ' + type +  ' Description: ' + typeOfResearchText"
							></v-checkbox>
							<div v-if="researchType && researchType.includes(this.$createStudyLists.researchTypes[5])">
								<v-text-field
									v-model="researchTypeOther"
									label="Other (specify)"
									hide-details="auto"
									outlined
									:aria-label="'Other (specify): ' + researchTypeOther"
								></v-text-field>
							</div>
							</fieldset>
						</v-col>
						<v-col md="6" cols="12">
							<explanation-box
								:color="'green'"
								:displayText="typeOfResearchText">
							</explanation-box>
						</v-col>
					</v-row>
					<v-divider inset></v-divider>
					<v-row>
						<v-col md="2">
							<h3 class="left-align">Have funds been received for this study?</h3>
						</v-col>
						<v-col md="4">
							<fieldset class="border-0">
							<v-radio-group
								v-model="ifFundsReceived"
								:rules="radioRules"
							>
								<v-radio
									v-for="(fundOption, i) in this.$createStudyLists.fundsReceivedOptions"
									:key="fundOption.key + i"
									:label="fundOption.value"
									:value="fundOption.key"
									:aria-label="'Have funds been received for this study? ' + fundOption.key +  ' Description: ' + fundsReceivedText"
								></v-radio>
										<div v-if="ifFundsReceived == (this.$createStudyLists.FUNDS_OTHER.key)">
											<v-text-field
												v-model="ifFundsReceivedOther"
												label="Other (specify)"
												hide-details="auto"
												outlined
												:aria-label="'Other (specify): ' + ifFundsReceivedOther"
											></v-text-field>
										</div>
							</v-radio-group>
							</fieldset>
						</v-col>
						<v-col md="6" cols="12">
							<explanation-box
								id="form-funds-received-explanation-box2"
								:color="'green'"
								:displayText="fundsReceivedText">
							</explanation-box>
						</v-col>
					</v-row>
				</div>
					<div v-if="ifFundsReceived && ifFundsReceived == this.$createStudyLists.FUNDS_YES.key && fundingSourcesCheckboxes.length">

					<v-divider inset></v-divider>
					<v-row>
						<v-col md="2">
							<h3 class="left-align">Funding source? (Check all that apply)</h3>
						</v-col>
						<v-col md="4">
							<fieldset class="border-0">
							<v-checkbox
								v-model="openSubFunds"
								:label="fundingSourcesCheckboxes[0].name"
								hide-details
								:aria-label="'Funding source? (Check all that apply): ' + fundingSourcesCheckboxes[0].name + ' Description: ' + fundingSourceText"
							></v-checkbox>
							<div class="sub-funds" v-if="openSubFunds && openSubFunds == true">
								<v-checkbox
									v-model="fundingSource"
									v-for="(subFund, i) in fundingSourcesCheckboxes[0].subCategories"
									:key="subFund.name + i"
									:label="subFund.name"
									:value="subFund.alias"
									:rules="checkboxRules"
									hide-details
								></v-checkbox>
								<br>
								<div v-if="fundingSource && fundingSource.includes(this.$createStudyLists.fundingSources[0].subCategories[5].alias)">
									<v-text-field
										v-model="fundingSourceNIHOther"
										label="Other (specify)"
										hide-details="auto"
										outlined
										:aria-label="'Other (specify): ' + fundingSourceNIHOther"
									></v-text-field>
								</div>
							</div>
							<v-checkbox
								v-model="openAgencies"
								:label="fundingSourcesCheckboxes[1].name"
								hide-details
								:aria-label="'Funding source? (Check all that apply): ' + fundingSourcesCheckboxes[1].name + ' Description: ' + fundingSourceText"
							></v-checkbox>
							<div class="sub-funds" v-if="openAgencies && openAgencies == true">
								<v-checkbox
									v-model="fundingSource"
									v-for="(subFund, i) in fundingSourcesCheckboxes[1].subCategories"
									:key="subFund.name + i"
									:label="subFund.name"
									:value="subFund.alias"
									:rules="checkboxRules"
									hide-details
								></v-checkbox>
							</div>
							<v-checkbox
								v-model="fundingSource"
								:label="fundingSourcesCheckboxes[2].name"
								:value="fundingSourcesCheckboxes[2].name"
								:rules="checkboxRules"
								:aria-label="'Funding source? (Check all that apply): ' + fundingSourcesCheckboxes[2].name + ' Description: ' + fundingSourceText"
							></v-checkbox>
							<div v-if="fundingSource && fundingSource.includes(this.$createStudyLists.fundingSources[2].name)">
								<v-text-field
									v-model="fundingSourceOther"
									label="Other (specify)"
									hide-details="auto"
									outlined
									:aria-label="'Other (specify): ' + fundingSourceOther"
								></v-text-field>
							</div>
							</fieldset>
						</v-col>
						<v-col md="6" cols="12">
							<explanation-box
								:color="'green'"
								:displayText="fundingSourceText">
							</explanation-box>
						</v-col>
					</v-row>
				</div>
				<div v-if="openSubFunds && openSubFunds == true">
					<v-divider inset></v-divider>
					<v-row>
						<v-col md="2">
							<h3 class="left-align">Grant or contract number:</h3>
						</v-col>
						<v-col md="4">
							<v-text-field
								v-model="studyToSave.grantContractNumber"
								hide-details="auto"
								outlined
								:rules="requiredFieldRule"
								id="form-contract-num"
								name="form-contract-num"
								:aria-label="'Grant or contract number: ' + studyToSave.grantContractNumber "
							></v-text-field>
						</v-col>
						<v-col md="6" cols="12">
						</v-col>
					</v-row>
				</div>
				<v-divider inset></v-divider>
				<v-row >
					<v-col md="2">
						<h3 class="left-align">Researcher affiliation of main researcher/PI (Check all that apply):</h3>
					</v-col>
					<v-col md="4">
						<fieldset class="border-0">
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[0]"
							:value="researcherAffiliationsCheckboxes[0]"
							:hide-details="true"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[0] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[1]"
							:value="researcherAffiliationsCheckboxes[1]"
							:hide-details="true"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[1] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<!-- Input box if above checkbox is selected -->
						<div v-if="researcherAffiliation && researcherAffiliation.includes(this.$createStudyLists.researcherAffiliations[1])">
							<br>
							<v-text-field
								v-model="researcherAffiliationAgency"
								label="Specify Agency Name"
								hide-details="auto"
								outlined
							></v-text-field>
						</div>
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[2]"
							:value="researcherAffiliationsCheckboxes[2]"
							:hide-details="true"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[2] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[3]"
							:value="researcherAffiliationsCheckboxes[3]"
							:hide-details="true"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[3] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[4]"
							:value="researcherAffiliationsCheckboxes[4]"
							:hide-details="true"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[4] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<!-- Input box if above checkbox is selected -->
						<div v-if="researcherAffiliation && researcherAffiliation.includes(this.$createStudyLists.researcherAffiliations[4])">
							<br>
							<v-text-field
								v-model="researcherAffiliationOrganization"
								label="Specify Organization Name"
								hide-details="auto"
								outlined
							></v-text-field>
						</div>
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[5]"
							:value="researcherAffiliationsCheckboxes[5]"
							:hide-details="true"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[5] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<!-- Input box if above checkbox is selected -->
						<div v-if="researcherAffiliation && researcherAffiliation.includes(this.$createStudyLists.researcherAffiliations[5])">
							<br>
							<v-text-field
								v-model="researcherAffiliationClient"
								label="Specify Client"
								hide-details="auto"
								outlined
							></v-text-field>
						</div>
						<v-checkbox
							v-model="researcherAffiliation"
							:label="researcherAffiliationsCheckboxes[6]"
							:value="researcherAffiliationsCheckboxes[6]"
							:hide-details="false"
							:rules="checkboxRules"
							:aria-label="'Researcher affiliation of main researcher/PI (Check all that apply): ' + researcherAffiliationsCheckboxes[6] +  ' Description: ' + researchAffiliationText"
						></v-checkbox>
						<!-- Input box if above checkbox is selected -->
						<div v-if="researcherAffiliation && researcherAffiliation.includes(this.$createStudyLists.researcherAffiliations[6])">
							<v-text-field
								v-model="researcherAffiliationOther"
								label="Other (specify)"
								hide-details="auto"
								outlined
								:aria-label="'Other (specify): ' + researcherAffiliationOther"
							></v-text-field>
						</div>
						</fieldset>
					</v-col>
					<v-col md="6" cols="12">
						<explanation-box
							:color="'green'"
							:fullBox=true
							:displayText="researchAffiliationText">
						</explanation-box>
					</v-col>
				</v-row>
				<v-card-actions>
					<v-btn
						text
						class="btn-default"
						@click="onOpenCancel()"
					>
						Cancel
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						:loading="loadingSave"
						text
						class="btn-default"
						@click="checkForm()"
					>
						Save Changes
					</v-btn>
				</v-card-actions>
			</v-form>
		</v-card>
		<!-- Cancel pop up -->
		<foe-dialog
      v-model="cancelDialog"
			:dialogW="cancelDialog"
      persistent
			max-width="600px"
			class="cancel-dialog"
    >
      <v-card class="cancel-confirm-card">
        <v-card-title>Cancel</v-card-title>
        <v-card-text>Are you sure you want to cancel?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
					<v-btn
            text
						class="btn-default"
            @click="cancelDialog = false"
          >
            No
          </v-btn>
          <v-btn
            text
						class="btn-default"
            @click="onCancel()"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </foe-dialog>
	</div>
</template>

<script>
import { utilityService } from '@/services/utilityService.js';
export default {
	name: 'StudyBasics',
	props: {
	},
	data: () => ({
		studyPurpose: '',
		studyCharacteristics: [],
		studyPurposeOther: '',
		researchType: [],
		researchTypeOther: '',
		ifFundsReceived: '',
		ifFundsReceivedOther: '',
		fundingSource: [],
		fundingSourceNIHOther: '',
		fundingSourceAgencyOther: '',
		fundingSourceOther: '',
		openSubFunds: false,
		openAgencies: false,
		researcherAffiliation: [],
		researcherAffiliationAgency: '',
		researcherAffiliationOrganization: '',
		researcherAffiliationClient: '',
		researcherAffiliationOther: '',
		studyNameRules: [
			v => v !== '' || 'This section is required',
			v => (v && v.length <= 40) || 'Max 40 characters'
		],
		studyDescriptionRules: [
			v => v !== '' || 'This section is required',
			v => (v && v.length <= 400) || 'Max 400 characters'
		],
		researchTypeCheckboxes: [],
		fundingSourcesCheckboxes: [],
		researcherAffiliationsCheckboxes: [],
		studyCharacteristicsCheckboxes: [],
		radioRules: [v => (v !== '' || 'This section is required')],
		checkboxRules: [v => ((v && v.length > 0) || 'This section is required')],
		requiredFieldRule: [v => (v && v.length > 0) || 'This section is required'],
		loadingSave: false,
		valid: true,
		saveSuccess: false,
		hasErrors: false,
		cancelDialog: false,
		studyUuid: '',
		tempStudyName: null,
		tempDescription: null,
		studyNameText: 'Provide a short study name to distinguish it from other studies that you’ve created. This is not seen by respondents.',
		studyDescriptionText: 'Briefly describe the study. This will be used internally to monitor use of the ASA24 system; data will only be reported at the aggregate level and never used to identify individual studies. This is not seen by respondents.<br><br>' +
														'These details may include general information about your study population or a novel administration of ASA24, for example. You can use this space to clarify any responses to “Study Characteristics” below that you feel are not adequately captured.',
		studyCharacteristicsText: 'Please note, the information you provide about your study population’s characteristics will only be used internally to monitor use of the ASA24 system, such as specific age groups, vulnerable populations, and for awareness of studies that may be using ASA24 in ways that NCI receives questions about, e.g. in low literacy populations or using interview-administration rather than self-administration. Data will only be reported at the aggregate level and never used to identify individual studies.',
		studyAbbreviationText: 'Specify a study abbreviation to uniquely identify your study in the ASA24 system. The study abbreviation is a combination of three to eight letters and cannot contain numbers or special characters. <br><br>It is used as a prefix for your respondent usernames. For this reason, it is a good idea to use an abbreviation that will be meaningful to your respondents. For example, if a study abbreviation is FRUIT, sample usernames include FRUIT101 or FRUIT1001.',
		purposeOfStudyText: 'Select the primary reason for creating this study.<br><br>This information is collected for internal tracking purposes only and does not affect study administration.',
		researchAffiliationText: 'Select the entity(ies) conducting the research. Check all that apply.<br><br>The purpose of the study is collected for internal tracking purposes only and does not affect study administration.',
		fundingSourceText: 'Specify the funding source and if it was funded by NIH, specify the institution and the grant or contract number.<br><br>This information is collected for internal tracking purposes only and does not affect study administration.',
		typeOfResearchText: 'Select the type of research method for this study.<br><br>The purpose of the study is collected for internal tracking purposes only and does not affect study administration.',
		fundsReceivedText: 'Specify whether funding was received for this study.<br><br>This information is collected for internal tracking purposes only and does not affect study administration.'
	}),
	mounted () {
		// Checkbox input lists must be copied in order to be able to use array functions
		this.researchTypeCheckboxes = this.$createStudyLists.researchTypes;
		this.fundingSourcesCheckboxes = this.$createStudyLists.fundingSources;
		this.researcherAffiliationsCheckboxes = this.$createStudyLists.researcherAffiliations;
		this.studyCharacteristicsCheckboxes = this.$createStudyLists.studyCharacteristics;
		// sets the values for the displayed properties from the selected study
		this.changeSelectedFields();
		this.$nextTick(function () {
			// 2021.10.20 - Sebastian Calle assisted with the following idea/solution
			// Code that will run only after the entire view has been rendered
			// var b = document.getElementById('form-study-purpose');
			// b.setAttribute('aria-labelledby', 'form-study-purpose-label')
			// 2021.10.25 - Yevgeniy editted above to target class name and remove aria-labelledby attribute for all elements
			// 2022.06.29 - CHT adding to edit study
			var vRadioGroup = document.getElementsByClassName('v-input--radio-group__input');
			for (let i = 0; i < vRadioGroup.length; i++) {
				vRadioGroup[i].removeAttribute('aria-labelledby');
			}
		});
		this.tempStudyName = this.studyToSave.studyName
		this.tempDescription = this.studyToSave.description
	},
	computed: {
		studyNameLabel: function () {
			return 'Study name: ' + this.tempStudyName + ' ' + this.tempStudyName.length + ' characters used. Max character limit is 40. Possible ' + (40 - this.tempStudyName.length) + ' characters remaining.';
		},
		descriptionLabel: function () {
			return 'Study description: ' + this.tempDescription + ' ' + this.tempDescription.length + ' characters used. Max character limit is 400. Possible ' + (400 - this.tempDescription.length) + ' characters remaining.';
		},
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.changeSelectedFields();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		},
		studyToSave: function () {
			// eslint-disable-next-line prefer-const
			let temp = null;
			try {
				temp = JSON.parse(JSON.stringify(this.$store.getters.getSelectedStudy));
			} catch (error) {
				console.log(`Error parsing getSelectedStudy ${error.message}`);
			}

			try {
				// Try and parse the funding json to an object. Allow for older studies that have an empty
				// string saved fort his field.
				if (temp.funding) {
					temp.funding = JSON.parse(temp.funding)
				} else {
					temp.funding = {};
				}
			} catch (error) {
				console.log(`Error parsing funding: ${error.message}`);
			}

			return temp;
		},
		studyPurposeToSave: function () {
			// If 'other' is selected the saved value should be the user input
			if (this.studyPurposeOther === '' || this.studyPurpose !== this.$createStudyLists.studyPurposes[4]) {
				return { selection: this.studyPurpose, value: this.studyPurpose };
			} else {
				return { selection: this.studyPurpose, value: this.studyPurposeOther };
			}
		},
		researchTypeToSave: function () {
			var temp = [];
			for (var i = 0; i < this.researchType.length; i++) {
				if (this.researchType[i] !== this.$createStudyLists.researchTypes[5]) {
					temp.push({ selection: this.researchType[i], value: this.researchType[i] })
				} else {
					temp.push({ selection: this.researchType[i], value: this.researchTypeOther })
				}
			}
			return temp;
		},
		fundingSourceToSave: function () {
			var temp = [];
			for (var i = 0; i < this.fundingSource.length; i++) {
				if (this.fundingSource[i] === this.$createStudyLists.fundingSources[2].name) {
					temp.push({ selection: this.fundingSource[i], value: this.fundingSourceOther });
				} else if (this.fundingSource[i] === this.$createStudyLists.fundingSources[0].subCategories[5].alias) {
					temp.push({ selection: this.fundingSource[i], value: this.fundingSourceNIHOther });
				} else if (this.fundingSource[i] === this.$createStudyLists.fundingSources[1].subCategories[4].alias) {
					temp.push({ selection: this.fundingSource[i], value: this.fundingSourceAgencyOther });
				} else {
					temp.push({ selection: this.fundingSource[i], value: this.fundingSource[i] });
				}
			}

			return temp;
		},
		researcherAffiliationToSave: function () {
			var temp = [];
			for (var i = 0; i < this.researcherAffiliation.length; i++) {
				if (this.researcherAffiliation[i] === this.$createStudyLists.researcherAffiliations[1]) {
					temp.push({ selection: this.researcherAffiliation[i], value: this.researcherAffiliationAgency });
				} else if (this.researcherAffiliation[i] === this.$createStudyLists.researcherAffiliations[4]) {
					temp.push({ selection: this.researcherAffiliation[i], value: this.researcherAffiliationOrganization });
				} else if (this.researcherAffiliation[i] === this.$createStudyLists.researcherAffiliations[5]) {
					temp.push({ selection: this.researcherAffiliation[i], value: this.researcherAffiliationClient });
				} else if (this.researcherAffiliation[i] === this.$createStudyLists.researcherAffiliations[6]) {
					temp.push({ selection: this.researcherAffiliation[i], value: this.researcherAffiliationOther });
				} else {
					temp.push({ selection: this.researcherAffiliation[i], value: this.researcherAffiliation[i] });
				}
			}

			return temp;
		}
	},
	watch: {
		openSubFunds: function (val) {
			if (val === false) {
				this.studyToSave.grantContractNumber = '';
				for (var i = 0; i < this.fundingSourcesCheckboxes[0].subCategories.length; i++) {
					const index = this.fundingSource.indexOf(this.fundingSourcesCheckboxes[0].subCategories[i].alias);
					if (index > -1) {
						this.fundingSource.splice(index, 1);
					}
				}
			}
		},
		fundsReceived: function (val) {
			if (val !== this.$createStudyLists.fundsReceivedOptions[0]) {
				this.fundingSource = [];
				this.fundingSourceOther = '';
				this.fundingSourceNIHOther = '';
				this.fundingSourceAgencyOther = '';
				this.studyToSave.grantContractNumber = '';
				this.openSubFunds = false;
				this.openAgencies = false;
			}
		},
		studyPurpose: function (newVal, oldVal) {
			// If the value changes from research, all values that depend on that selection should be reset
			if (newVal !== this.$createStudyLists.studyPurposes[0] && oldVal === this.$createStudyLists.studyPurposes[0]) {
				this.researchType = [];
				this.ifFundsReceived = '';
				this.fundingSource = [];
				this.fundingSourceOther = '';
				this.fundingSourceNIHOther = '';
				this.fundingSourceAgencyOther = '';
				this.openSubFunds = false;
				this.openAgencies = false;
				this.studyToSave.grantContractNumber = '';
				this.researchTypeOther = '';
			}

			// If the 'other' option is unselected, the other field should be reset to no value
			if (oldVal === this.$createStudyLists.studyPurposes[4]) {
				this.studyPurposeOther = '';
			}
		},
		ifFundsReceived: function (newVal, oldVal) {
			if (newVal !== this.$createStudyLists.FUNDS_YES.key && oldVal === this.$createStudyLists.FUNDS_YES.key) {
				this.fundingSource = [];
				this.fundingSourceOther = '';
				this.fundingSourceNIHOther = '';
				this.fundingSourceAgencyOther = '';
				this.openSubFunds = false;
				this.openAgencies = false;
				this.contractNum = '';
				this.researchTypeOther = '';
			}
			// If the 'other' option is unselected, the other field should be reset to no value
			if (oldVal === this.$createStudyLists.FUNDS_OTHER.key) {
				this.ifFundsReceivedOther = '';
			}
		},
		researchType: function (newVal, oldVal) {
			if (oldVal && oldVal.includes(this.$createStudyLists.researchTypes[5]) && !newVal.includes(this.$createStudyLists.researchTypes[5])) {
				this.researchTypeOther = '';
			}
		},
		fundingSource: function (newVal, oldVal) {
			if (oldVal && oldVal.includes(this.$createStudyLists.fundingSources[2].name) && !newVal.includes(this.$createStudyLists.fundingSources[2].name)) {
				this.fundingSourceOther = '';
			}

			if (oldVal && oldVal.includes(this.$createStudyLists.fundingSources[0].subCategories[5].alias) && !newVal.includes(this.$createStudyLists.fundingSources[0].subCategories[5].alias)) {
				this.fundingSourceNIHOther = '';
			}

			if (oldVal && oldVal.includes(this.$createStudyLists.fundingSources[1].subCategories[4].alias) && !newVal.includes(this.$createStudyLists.fundingSources[1].subCategories[4].alias)) {
				this.fundingSourceAgencyOther = '';
			}
		},
		researcherAffiliation: function (newVal, oldVal) {
			if (oldVal && oldVal.includes(this.$createStudyLists.researcherAffiliations[1]) && !newVal.includes(this.$createStudyLists.researcherAffiliations[1])) {
				this.researcherAffiliationAgency = '';
			} else if (oldVal && oldVal.includes(this.$createStudyLists.researcherAffiliations[4]) && !newVal.includes(this.$createStudyLists.researcherAffiliations[4])) {
				this.researcherAffiliationOrganization = '';
			} else if (oldVal && oldVal.includes(this.$createStudyLists.researcherAffiliations[5]) && !newVal.includes(this.$createStudyLists.researcherAffiliations[5])) {
				this.researcherAffiliationClient = '';
			} else if (oldVal && oldVal.includes(this.$createStudyLists.researcherAffiliations[6]) && !newVal.includes(this.$createStudyLists.researcherAffiliations[6])) {
				this.researcherAffiliationOther = '';
			}
		}
	},
	methods: {
		async checkTabMatch ({ tabIndex, subTabIndex } = {}) {
			var tabsPos = await utilityService.getTabsPositionByName('Edit Study Basics', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				setTimeout(() => {
					this.$refs.firstFocusable.focus();
				}, '450');
			}
		},
		onOpenCancel () {
			this.cancelDialog = true;
		},
		onCancel () {
			this.$router.push('/landing');
		},
		async checkForm () {
			this.loadingSave = true;
			var valid = this.$refs.form.validate();
			window.scrollTo(0, 0);
			const fundingObject = {};
			fundingObject.listOfFunds = this.fundingSourceToSave
			// fundingObject.selection = this.studyToSave.funding.selection
			// fundingObject.otherText = this.studyToSave.funding.otherText
			fundingObject.selection = this.ifFundsReceived
			fundingObject.otherText = this.ifFundsReceivedOther
			if (!valid) {
				console.log('There are errors');
				this.loadingSave = false;
				this.hasErrors = true;
				this.saveSuccess = false;
			} else {
				this.studyToSave.purposeType = JSON.stringify(this.studyPurposeToSave);
				this.studyToSave.researchType = JSON.stringify(this.researchTypeToSave);
				this.studyToSave.sourceList = JSON.stringify(this.fundingSourceToSave); // sourceList is same as funding for now unless further directed
				// this.studyToSave.sourceList = ''; // empty string for now
				this.studyToSave.funding = JSON.stringify(fundingObject);
				this.studyToSave.organizationType = JSON.stringify(this.researcherAffiliationToSave);
				this.studyToSave.studyCharacteristics = JSON.stringify(this.studyCharacteristics);
				// make sure that time values are in unix format
				var unixStartDate = new Date(this.studyToSave.dateStart);
				var unixEndDate = new Date(this.studyToSave.dateEnd);
				this.studyToSave.dateEnd = unixEndDate / 1000;
				this.studyToSave.dateStart = unixStartDate / 1000;

				const me = this;

				// save form if there are no errors
				await this.$store.dispatch('updateStudy', this.studyToSave)
					.then(async function (response) {
						if (response) {
							me.saveSuccess = true;
							me.hasErrors = false;
						}
					});

				this.loadingSave = false;
			}
		},
		changeSelectedFields () {
			// Fill in fields that may contain 'other' as the choice
			this.studyUuid = this.studyToSave.studyUuid;
			this.studyPurpose = '';
			this.studyCharacteristics = JSON.parse(this.studyToSave.studyCharacteristics);
			var studyPurpose = JSON.parse(this.studyToSave.purposeType);
			this.studyPurpose = studyPurpose.selection;
			if (this.studyPurpose === this.$createStudyLists.studyPurposes[4]) {
				this.studyPurposeOther = studyPurpose.value;
			}

			var researchType = [];
			this.researchType = [];
			researchType = JSON.parse(this.studyToSave.researchType);
			if (researchType && Array.isArray(researchType)) {
				for (var i = 0; i < researchType.length; i++) {
					this.researchType.push(researchType[i].selection);
					if (researchType[i].selection === this.$createStudyLists.researchTypes[5]) {
						this.researchTypeOther = researchType[i].value;
					}
				}
			}

			this.ifFundsReceived = this.studyToSave.funding.selection
			this.ifFundsReceivedOther = this.studyToSave.funding.otherText

			// var fundingSource = [];
			this.fundingSource = [];
			const listOfFunds = this.studyToSave.funding.listOfFunds;

			if (listOfFunds && Array.isArray(listOfFunds)) {
				for (var j = 0; j < listOfFunds.length; j++) {
					this.fundingSource.push(listOfFunds[j].selection);

					// If any of the NIH options are selected, the higher level NIH checkbox must be selected
					if (listOfFunds[j].value && listOfFunds[j].value.includes('NIH')) {
						this.openSubFunds = true;
					}

					if (listOfFunds[j].value && listOfFunds[j].value.includes('Other US Government Agency')) {
						this.openAgencies = true;
					}

					if (listOfFunds[j].selection === this.$createStudyLists.fundingSources[0].subCategories[5].alias) {
						this.fundingSourceNIHOther = listOfFunds[j].value;
					} else if (this.fundingSource[j] === this.$createStudyLists.fundingSources[2].name) {
						this.fundingSourceOther = listOfFunds[j].value;
					}
				}
			}

			var affiliation = [];
			this.researcherAffiliation = [];
			affiliation = JSON.parse(this.studyToSave.organizationType);
			for (var k = 0; k < affiliation.length; k++) {
				this.researcherAffiliation.push(affiliation[k].selection);
				if (affiliation[k].selection === this.$createStudyLists.researcherAffiliations[1]) {
					this.researcherAffiliationAgency = affiliation[k].value;
				} else if (affiliation[k].selection === this.$createStudyLists.researcherAffiliations[4]) {
					this.researcherAffiliationOrganization = affiliation[k].value;
				} else if (affiliation[k].selection === this.$createStudyLists.researcherAffiliations[5]) {
					this.researcherAffiliationClient = affiliation[k].value;
				} else if (affiliation[k].selection === this.$createStudyLists.researcherAffiliations[6]) {
					this.researcherAffiliationOther = affiliation[k].value;
				}
			}
		},
		studyNameChar (e) {
			this.tempStudyName = e.target.value
		},
		descriptionChar (e) {
			this.tempDescription = e.target.value
		},
		focusToTop () {
			var myHeader = document.getElementById('skipToMain')
			myHeader.focus()
		}
	}
}
</script>

<style>
</style>
